import { Crosshair, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { parseDataMatrix } from "@/lib/dm-parser";
import { useScanCapture } from "@/lib/use-scan-capture";
import { cn } from "@/lib/utils";

/**
 * «Проверка сканера» — диагностика без бэкенда, для любого вошедшего и в обеих
 * инсталляциях.
 *
 * Зачем: кольцо-сканер на телефоне (Bluetooth HID) и встроенный сканер ТСД
 * доставляют код по-разному, а на скан-экранах видно только итог — «принял /
 * молчит». Здесь видно всё по дороге: что приходит в `keydown` (`key`, `code`,
 * Shift, интервал), что в фокусе (на iPhone скрытое поле без касания фокус не
 * получает), и какой код из этого собрал ТОТ ЖЕ перехват `useScanCapture`, что
 * стоит на всех скан-экранах. Своего разбора нажатий страница не делает — иначе
 * проверяла бы не то, чем работает склад.
 */

const HISTORY_MAX = 10;
const KEYLOG_MAX = 60;

interface ScanRecord {
  id: number;
  raw: string;
  /** Пришёл при выключенном перехвате (здесь он включён всегда — на всякий). */
  blocked: boolean;
  at: string;
}

interface KeyRecord {
  id: number;
  key: string;
  code: string;
  keyCode: number;
  shift: boolean;
  /** мс от предыдущего нажатия; null — первое. */
  dt: number | null;
  /** `document.activeElement` в момент нажатия. */
  target: string;
}

function showKey(k: string): string {
  if (k === " ") return "␣";
  return k;
}

export function ScanTestPage(): React.ReactElement {
  const [history, setHistory] = useState<ScanRecord[]>([]);
  const [keys, setKeys] = useState<KeyRecord[]>([]);
  const [focused, setFocused] = useState("—");
  const manualRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);
  const lastKeyAt = useRef<number | null>(null);

  const record = useCallback((raw: string, blocked: boolean) => {
    seq.current += 1;
    const at = new Date().toLocaleTimeString("ru-RU");
    const rec: ScanRecord = { id: seq.current, raw, blocked, at };
    setHistory((h) => [rec, ...h].slice(0, HISTORY_MAX));
  }, []);

  const { scanInputRef, scanInputProps } = useScanCapture({
    enabled: true,
    onScan: (raw) => record(raw, false),
    onBlocked: (raw) => record(raw, true),
  });

  const describe = useCallback(
    (el: Element | null): string => {
      if (!el || el === document.body) return "ничего (BODY) — код ловит запасной буфер";
      if (el === scanInputRef.current) return "скрытое поле сканера";
      if (el === manualRef.current) return "поле ручного ввода";
      return el.tagName;
    },
    [scanInputRef],
  );

  // Что в фокусе — по событиям фокуса; `focusout` приходит ДО смены
  // `activeElement`, поэтому читаем его следующим кадром.
  useEffect(() => {
    const update = () => setFocused(describe(document.activeElement));
    const later = () => requestAnimationFrame(update);
    update();
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", later);
    return () => {
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", later);
    };
  }, [describe]);

  // Сырой журнал нажатий — в фазе захвата, раньше любого обработчика.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const prev = lastKeyAt.current;
      lastKeyAt.current = e.timeStamp;
      seq.current += 1;
      const rec: KeyRecord = {
        id: seq.current,
        key: e.key,
        code: e.code,
        keyCode: e.keyCode,
        shift: e.shiftKey,
        dt: prev === null ? null : Math.round(e.timeStamp - prev),
        target: describeTag(document.activeElement, scanInputRef.current),
      };
      setKeys((k) => [rec, ...k].slice(0, KEYLOG_MAX));
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [scanInputRef]);

  const last = history[0];
  const dm = last ? parseDataMatrix(last.raw) : null;

  return (
    <div className="mx-auto max-w-md space-y-3 p-4">
      {/* Скрытое поле перехвата — то же, что на скан-экранах. */}
      <input
        ref={scanInputRef}
        {...scanInputProps}
        className="pointer-events-none fixed left-0 top-0 size-px opacity-0"
      />

      <div>
        <h1 className="text-xl font-semibold">Проверка сканера</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Отсканируйте любой код кольцом или сканером ТСД. На кольце задержка между
          символами — 10 мс: при 1 мс iOS теряет символы.
        </p>
      </div>

      <div className="space-y-1 rounded-lg border bg-card p-3 text-sm" data-testid="scan-status">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
            перехват включён
          </span>
        </div>
        <div>
          В фокусе: <b>{focused}</b>
        </div>
        <Button
          type="button"
          variant="outline"
          className="mt-1 h-11 w-full"
          // Фокус ставится ВНУТРИ обработчика касания: iOS выполняет `focus()`
          // только в ответ на жест пользователя.
          onClick={() => scanInputRef.current?.focus({ preventScroll: true })}
        >
          <Crosshair className="size-4" /> Поставить фокус на поле сканера
        </Button>
      </div>

      <section className="rounded-lg border bg-card p-3" data-testid="last-scan">
        <h2 className="text-xs font-semibold uppercase text-muted-foreground">Последний код</h2>
        {last ? (
          <div className="mt-1 space-y-1 text-sm">
            <div className="break-all font-mono text-[15px] font-semibold">{last.raw}</div>
            <div className="text-muted-foreground">
              длина {last.raw.length}
              {last.blocked && <span className="ml-2 font-semibold text-red-700">не принят перехватом</span>}
            </div>
            {dm ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 font-mono text-[13px]">
                <dt className="text-muted-foreground">GTIN</dt>
                <dd className="break-all">{dm.gtin}</dd>
                <dt className="text-muted-foreground">серийник</dt>
                <dd className="break-all">{dm.serial}</dd>
                <dt className="text-muted-foreground">крипто</dt>
                <dd className="break-all">{dm.crypto ?? "—"}</dd>
              </dl>
            ) : (
              <div className="text-muted-foreground">не ЧЗ</div>
            )}
          </div>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">Сканов ещё не было.</p>
        )}
      </section>

      {history.length > 1 && (
        <section className="rounded-lg border bg-card p-3">
          <h2 className="text-xs font-semibold uppercase text-muted-foreground">
            Предыдущие ({history.length - 1})
          </h2>
          <ol className="mt-1 space-y-1">
            {history.slice(1).map((h) => (
              <li key={h.id} className="flex gap-2 text-[12px]">
                <span className="shrink-0 text-muted-foreground">{h.at}</span>
                <span className={cn("break-all font-mono", h.blocked && "text-red-700")}>{h.raw}</span>
                <span className="ml-auto shrink-0 text-muted-foreground">{h.raw.length}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className="rounded-lg border bg-card p-3">
        <label htmlFor="scan-test-manual" className="text-xs font-semibold uppercase text-muted-foreground">
          Ручной ввод
        </label>
        <p className="text-[12px] text-muted-foreground">
          Набранное здесь не должно попадать в «Последний код».
        </p>
        <Input id="scan-test-manual" ref={manualRef} className="mt-1" autoComplete="off" />
      </section>

      <section className="rounded-lg border bg-card p-3" data-testid="key-log">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase text-muted-foreground">
            Нажатия ({keys.length})
          </h2>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setKeys([]);
              lastKeyAt.current = null;
            }}
          >
            <Trash2 className="size-4" /> Очистить
          </Button>
        </div>
        {keys.length === 0 ? (
          <p className="text-sm text-muted-foreground">Нажатий ещё не было.</p>
        ) : (
          <table className="mt-1 w-full table-fixed font-mono text-[11px]">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="w-[18%] font-normal">key</th>
                <th className="w-[28%] font-normal">code</th>
                <th className="w-[12%] font-normal">⇧</th>
                <th className="w-[14%] font-normal">Δ мс</th>
                <th className="font-normal">куда</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-t">
                  <td className="truncate">{showKey(k.key)}</td>
                  <td className="truncate">
                    {k.code || <span className="text-red-700">пусто</span>}
                    {k.keyCode === 229 && <span className="text-amber-700"> 229</span>}
                  </td>
                  <td>{k.shift ? "да" : ""}</td>
                  <td>{k.dt ?? ""}</td>
                  <td className="truncate">{k.target}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

/** Что было в фокусе в момент нажатия — коротко, для узкой колонки. */
function describeTag(el: Element | null, scanInput: HTMLInputElement | null): string {
  if (!el) return "—";
  if (el === scanInput) return "скрытое";
  return el.tagName.toLowerCase();
}
